/**
 * Mobile Petty Cash — Approvals Queue.
 * Approve / Reject pending requests inline. Approver-only screen.
 */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowLeft, CheckCircle2, XCircle, Loader2, ShieldCheck, MapPin, AlertTriangle } from 'lucide-react';
import { api, apiGet } from '../../lib/api';
import { formatINR, formatDate } from '../../lib/format';

/** Bill image for the approval card — fetched as an auth-gated blob, tap to
 *  enlarge full-screen so the manager can actually verify the bill. */
function BillThumb({ url }) {
  const [src, setSrc] = useState(null);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    let obj = null; let cancelled = false;
    if (!url) return undefined;
    (async () => {
      try {
        const r = await api.get(url.startsWith('/api') ? url.replace(/^\/api/, '') : url, { responseType: 'blob' });
        if (cancelled) return;
        obj = URL.createObjectURL(r.data);
        setSrc(obj);
      } catch { /* no thumb */ }
    })();
    return () => { cancelled = true; if (obj) URL.revokeObjectURL(obj); };
  }, [url]);
  if (!src) return null;
  return (
    <>
      <button type="button" onClick={() => setOpen(true)} className="block w-full mt-2" data-testid="m-pc-bill-thumb">
        <img src={src} alt="Bill" className="w-full max-h-44 object-cover rounded-md border border-slate-200" />
        <div className="text-[10px] text-slate-400 mt-0.5 text-center">Tap bill to enlarge & verify</div>
      </button>
      {open && (
        <div className="fixed inset-0 z-[60] bg-black/85 flex items-center justify-center p-3" onClick={() => setOpen(false)} data-testid="m-pc-bill-modal">
          <img src={src} alt="Bill" className="max-w-full max-h-[90vh] object-contain rounded-sm bg-white" />
        </div>
      )}
    </>
  );
}

export default function MobilePettyCashApprovals() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  const load = () => {
    setLoading(true);
    apiGet('/petty-cash/requests', { status: 'pending', limit: 100 })
      .then((d) => setItems(d?.items || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const approve = async (rid) => {
    setBusyId(rid);
    try {
      await api.put(`/petty-cash/requests/${rid}/approve`);
      toast.success('Approved');
      setItems((xs) => xs.filter((x) => x.id !== rid));
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Approve failed');
    } finally { setBusyId(null); }
  };

  const reject = async (rid) => {
    const reason = window.prompt('Reason for rejection (min 4 chars):');
    if (!reason || reason.trim().length < 4) {
      toast.error('Reason must be at least 4 characters');
      return;
    }
    setBusyId(rid);
    try {
      await api.put(`/petty-cash/requests/${rid}/reject`, null, { params: { reason } });
      toast.success('Rejected');
      setItems((xs) => xs.filter((x) => x.id !== rid));
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Reject failed');
    } finally { setBusyId(null); }
  };

  return (
    <div className="min-h-screen bg-slate-50 pb-6" data-testid="m-petty-cash-approvals">
      <div className="bg-[#1F7A6E] text-white px-4 py-3 flex items-center gap-3 sticky top-0 z-10">
        <Link to="/m/petty-cash" className="p-1.5 -ml-1.5"><ArrowLeft size={20} /></Link>
        <div>
          <div className="font-display font-bold flex items-center gap-1.5"><ShieldCheck size={14} /> Approvals</div>
          <div className="text-[10px] text-slate-300">{items.length} pending</div>
        </div>
      </div>

      <div className="p-4 space-y-2">
        {loading ? (
          <div className="text-center text-slate-500 text-sm py-6">Loading…</div>
        ) : items.length === 0 ? (
          <div className="text-center text-slate-500 text-sm py-12">
            <CheckCircle2 size={36} className="mx-auto mb-2 text-emerald-500" />
            All caught up · nothing pending
          </div>
        ) : items.map((r) => (
          <div key={r.id} className="bg-white border border-slate-200 rounded-xl p-3" data-testid={`m-pc-approval-${r.id}`}>
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0 flex-1">
                <div className="font-display font-bold text-sm text-slate-900 truncate">{r.vendor_name || '—'}</div>
                <div className="text-[11px] text-slate-500 truncate">{r.submitted_by_name || '—'} · {r.project_name || '—'}</div>
                <div className="text-[10px] text-slate-500 truncate mt-0.5">{r.category} · {r.payment_mode}</div>
              </div>
              <div className="text-right">
                <div className="font-display font-bold text-base text-slate-900">{formatINR(r.actual_amount || r.estimated_amount || 0)}</div>
                <div className="text-[10px] text-slate-500">{formatDate(r.expense_date)}</div>
              </div>
            </div>
            {r.reason && <div className="mt-2 text-[11px] text-slate-700 bg-slate-50 rounded-md p-2 line-clamp-2">{r.reason}</div>}
            {r.invoice_image_url && <BillThumb url={r.invoice_image_url} />}
            {(r.ai_risk_score >= 60 || r.geofence_outside) && (
              <div className="mt-1.5 text-[10px] flex items-center gap-2 text-rose-600">
                {r.ai_risk_score >= 60 && <span className="flex items-center gap-1"><AlertTriangle size={10} /> AI risk {r.ai_risk_score}</span>}
                {r.geofence_outside && <span className="flex items-center gap-0.5"><MapPin size={10} /> outside geofence</span>}
              </div>
            )}
            <div className="mt-2.5 grid grid-cols-2 gap-2">
              <button
                onClick={() => reject(r.id)}
                disabled={busyId === r.id}
                data-testid={`m-pc-reject-${r.id}`}
                className="bg-white border border-rose-300 text-rose-700 font-display font-bold rounded-sm py-2 flex items-center justify-center gap-1.5 text-xs disabled:opacity-50"
              >
                {busyId === r.id ? <Loader2 size={12} className="animate-spin" /> : <XCircle size={14} />} Reject
              </button>
              <button
                onClick={() => approve(r.id)}
                disabled={busyId === r.id}
                data-testid={`m-pc-approve-${r.id}`}
                className="bg-emerald-600 hover:bg-emerald-700 text-white font-display font-bold rounded-sm py-2 flex items-center justify-center gap-1.5 text-xs disabled:opacity-50"
              >
                {busyId === r.id ? <Loader2 size={12} className="animate-spin" /> : <CheckCircle2 size={14} />} Approve
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
